import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useI18n();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-0 hero-glow px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft style={{ width: 16, height: 16 }} /> {t("login.back")}
          </button>
          <LangToggle />
        </div>
        <div className="card-base p-8 elev-2">
          <div className="flex justify-center mb-6"><Logo size="lg" /></div>
          <h1 className="text-2xl font-display font-semibold text-center tracking-tight">{t("login.welcome")}</h1>
          <p className="text-sm text-muted-foreground text-center mt-2 mb-8">
            {t("login.subtitle")}
          </p>
          <Button
            data-testid="google-login-button"
            onClick={handleGoogle}
            className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100 font-semibold text-base"
          >
            <svg className="mr-3" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("login.google")}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-6">
            {t("login.agree")}{" "}
            <a href="/terms" className="text-cyan-300 hover:underline">{t("login.terms")}</a> {t("login.and")}{" "}
            <a href="/privacy" className="text-cyan-300 hover:underline">{t("login.privacy")}</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
